import { motion } from "framer-motion";
import { Tag, RefreshCw } from "lucide-react";

const STEPS = [
  {
    year: "Année 1",
    title: "Vente initiale",
    desc: "Vous vendez la licence Norton à votre client. Une seule action commerciale.",
    highlight: true,
    badge: "Vente",
  },
  {
    year: "Année 2",
    title: "Revenu passif",
    desc: "Vous percevez 25 % de remise revendeur sur le renouvellement automatique.",
    badge: "+25%",
  },
  {
    year: "Année 3",
    title: "Revenu passif",
    desc: "Le renouvellement se reproduit, sans gestion ni relance de votre part.",
    badge: "+25%",
  },
  {
    year: "Année 4",
    title: "Revenu passif",
    desc: "Norton continue de gérer le cycle, vous continuez d'être rémunéré.",
    badge: "+25%",
  },
];

export default function Timeline() {
  return (
    <section id="timeline" className="relative py-28 lg:py-36 px-6 md:px-12" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-norton-yellow">
            Un modèle sur 4 ans
          </span>
          <h2 className="text-3xl sm:text-5xl font-heading font-semibold tracking-tight mt-4 leading-tight">
            Un revenu récurrent <br className="hidden sm:block" /> sur chaque client acquis
          </h2>
          <p className="text-zinc-400 mt-5 text-lg leading-relaxed">
            Vous créez un revenu récurrent sans charge de travail supplémentaire après
            la première vente. Voici comment se déroule le cycle.
          </p>
        </div>

        {/* Desktop horizontal line */}
        <div className="relative">
          <div className="hidden lg:block absolute top-[58px] left-0 right-0 h-px bg-gradient-to-r from-norton-yellow via-norton-yellow/40 to-norton-yellow/10" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative"
                data-testid={`timeline-step-${i + 1}`}
              >
                <div className="flex items-center gap-4 mb-6 lg:mb-8">
                  <div
                    className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border ${
                      s.highlight
                        ? "bg-norton-yellow border-norton-yellow text-black glow-yellow"
                        : "bg-card border-white/15 text-norton-yellow"
                    }`}
                  >
                    {s.highlight ? <Tag className="h-5 w-5" /> : <RefreshCw className="h-5 w-5" />}
                  </div>
                  <span className="lg:hidden text-sm font-semibold text-norton-yellow uppercase tracking-wider">
                    {s.year}
                  </span>
                </div>

                <div className="rounded-2xl border border-white/10 bg-card/60 backdrop-blur p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-norton-yellow/30">
                  <div className="flex items-center justify-between mb-3">
                    <span className="hidden lg:block text-xs font-bold uppercase tracking-widest text-zinc-500">
                      {s.year}
                    </span>
                    <span className="ml-auto rounded-full bg-norton-yellow/10 px-3 py-1 text-xs font-bold text-norton-yellow">
                      {s.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-12 text-center text-zinc-400 text-base">
          👉 Résultat : <span className="text-white font-medium">une vente = plusieurs années de revenus</span>, sans effort supplémentaire.
        </p>
      </div>
    </section>
  );
}
